import {
  roundHalfUpCents,
  type BidInput,
  type Cents,
  type MaterialLine,
  type PricingConfig,
} from '@lbhl/shared';

export interface MaterialDrivers {
  totalLinearFeet: number;
  totalStrands: number;
  runCount: number;
  hasAccent: boolean;
  /**
   * Engineering counts for this tier, per SKU, already accumulated through every
   * lower tier. Supplies the quantity for `manual_entry` rules, which is the only
   * driver whose number cannot be derived from the measurements.
   */
  manualCounts: Map<string, number>;
}

/**
 * Derives estimated material cost from the bill-of-materials rules.
 *
 * This never touches the customer price. The per-foot rate already covers
 * materials; this figure exists so admins can see margin and analyse pricing.
 * It is labelled "excludes labour" everywhere it surfaces, because it does.
 */
export function computeMaterials(
  config: PricingConfig,
  input: BidInput,
  drivers: MaterialDrivers,
): { materials: MaterialLine[]; estMaterialCostCents: Cents; warnings: string[] } {
  const itemsBySku = new Map(config.inventoryItems.map((i) => [i.sku, i]));
  const warnings: string[] = [];
  const qtyBySku = new Map<string, number>();

  for (const rule of config.bomRules) {
    const when = rule.appliesWhen;
    if (when) {
      if (when.colorCode !== undefined && when.colorCode !== input.colorCode) continue;
      if (when.clipColorCode !== undefined && when.clipColorCode !== input.clipColorCode) continue;
      if (when.requiresAccent === true && !drivers.hasAccent) continue;
    }

    const qty = ruleQuantity(rule.driver, rule.sku, rule.qtyPerDriver, drivers);
    if (qty <= 0) continue;

    if (!itemsBySku.has(rule.sku)) {
      warnings.push(`BOM rule references unknown inventory SKU "${rule.sku}".`);
      continue;
    }
    qtyBySku.set(rule.sku, (qtyBySku.get(rule.sku) ?? 0) + qty);
  }

  const materials: MaterialLine[] = [];
  let estMaterialCostCents = 0;

  // Iterate inventory order, not rule order, so the output is stable and
  // reproducible regardless of how rules were entered in the admin UI.
  for (const item of config.inventoryItems) {
    const qty = qtyBySku.get(item.sku);
    if (qty === undefined || qty <= 0) continue;
    const extendedCostCents = roundHalfUpCents(qty * item.unitCostCents);
    materials.push({
      sku: item.sku,
      name: item.name,
      qty: Number(qty.toFixed(4)),
      uom: item.uom,
      unitCostCents: item.unitCostCents,
      extendedCostCents,
    });
    estMaterialCostCents += extendedCostCents;
  }

  // Surface gaps in the cost model rather than quietly reporting a rosy margin.
  const unruled = config.inventoryItems
    .filter((i) => !config.bomRules.some((r) => r.sku === i.sku))
    .map((i) => i.name);
  if (unruled.length > 0) {
    warnings.push(
      `Material cost excludes ${unruled.length} inventory item(s) with no BOM rule yet: ${unruled.join(', ')}.`,
    );
  }

  return { materials, estMaterialCostCents, warnings };
}

function ruleQuantity(
  driver: string,
  sku: string,
  qtyPerDriver: number,
  drivers: MaterialDrivers,
): number {
  switch (driver) {
    case 'per_measured_foot':
      return drivers.totalLinearFeet * qtyPerDriver;
    case 'per_strand':
      return drivers.totalStrands * qtyPerDriver;
    case 'per_run':
      // A "run" is a measured footage-bearing surface with footage on it. This
      // is a proxy until circuit planning is modelled properly.
      return drivers.runCount * qtyPerDriver;
    case 'per_bid':
      return qtyPerDriver;
    case 'manual_entry':
      // The bidder counted these on the Engineering section. Zero when they have
      // not, which is correct: an uncounted consumable contributes no cost rather
      // than a guessed one.
      return (drivers.manualCounts.get(sku) ?? 0) * qtyPerDriver;
    default:
      return 0;
  }
}
